/**
 * Reimbursement Report Page - Comprehensive dashboard for admin/HR
 */

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { reimbursementApi } from '../api/reimbursementApi'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'
import type { ReportFilters, ClaimStatus } from '../types/reimbursement.types'

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

const STATUS_OPTIONS: { value: ClaimStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
]

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  submitted: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  under_review: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  approved: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-300' },
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const ReimbursementReportPage = () => {
  const { user } = useAuth()
  const currentYear = new Date().getFullYear()

  // Check if user can access reports
  const canAccessReports = user?.roles?.some(role =>
    role.name === 'admin'
  ) || user?.division_name?.toLowerCase() === 'finance'

  // Filter state
  const [filters, setFilters] = useState<ReportFilters>({
    year: currentYear,
  })
  const [employeeSearch, setEmployeeSearch] = useState('')
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false)
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('')

  // Active tab
  const [activeTab, setActiveTab] = useState<'overview' | 'details'>('overview')

  // Claims pagination
  const [claimsPage, setClaimsPage] = useState(1)

  // Fetch comprehensive report
  const { data: reportData, isLoading: isLoadingReport } = useQuery({
    queryKey: ['reimbursementReport', filters],
    queryFn: () => reimbursementApi.getComprehensiveReport(filters),
    enabled: canAccessReports,
  })

  // Fetch employees for dropdown
  const { data: employeesData } = useQuery({
    queryKey: ['reportEmployees', employeeSearch],
    queryFn: () => reimbursementApi.getReportEmployees(employeeSearch),
    enabled: canAccessReports && employeeSearch.length >= 2,
  })

  // Fetch departments for dropdown
  const { data: departmentsData } = useQuery({
    queryKey: ['reportDepartments'],
    queryFn: () => reimbursementApi.getReportDepartments(),
    enabled: canAccessReports,
  })

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['reimbursementCategories'],
    queryFn: () => reimbursementApi.getCategories(),
    enabled: canAccessReports,
  })

  // Fetch claims for detail tab
  const { data: claimsData, isLoading: isLoadingClaims } = useQuery({
    queryKey: ['reportClaims', filters, claimsPage],
    queryFn: () => reimbursementApi.getReportClaims({
      ...filters,
      page: claimsPage,
      limit: 20,
      sort_by: 'created_at',
      sort_order: 'desc',
    }),
    enabled: canAccessReports && activeTab === 'details',
  })

  const report = reportData?.data
  const employees = employeesData?.data || []
  const departments = departmentsData?.data || []
  const categories = categoriesData?.data || []
  const claims = claimsData?.data || []
  const pagination = claimsData?.pagination

  // Generate year options (current year and 5 years back)
  const yearOptions = useMemo(() => {
    const years = []
    for (let y = currentYear; y >= currentYear - 5; y--) {
      years.push(y)
    }
    return years
  }, [currentYear])

  // Calculate max amount for progress bars
  const maxCategoryAmount = useMemo(() => {
    if (!report?.by_category?.length) return 0
    return Math.max(...report.by_category.map(c => c.amount))
  }, [report?.by_category])

  const maxEmployeeAmount = useMemo(() => {
    if (!report?.by_employee?.length) return 0
    return Math.max(...report.by_employee.map(e => e.amount))
  }, [report?.by_employee])

  const maxDepartmentAmount = useMemo(() => {
    if (!report?.by_department?.length) return 0
    return Math.max(...report.by_department.map(d => d.amount))
  }, [report?.by_department])

  // Calculate max for monthly trends
  const maxMonthlyAmount = useMemo(() => {
    if (!report?.monthly_trends?.length) return 0
    return Math.max(...report.monthly_trends.map(m => Math.max(m.submitted, m.approved, m.paid)))
  }, [report?.monthly_trends])

  if (!canAccessReports) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <svg className="w-12 h-12 mx-auto text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
          <p className="text-red-600">Only Admin, HR, and Finance users can access reimbursement reports.</p>
        </div>
      </div>
    )
  }

  if (isLoadingReport && !report) {
    return <LoadingPage />
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
          <Link to="/reimbursement" className="hover:text-red-600">Reimbursement</Link>
          <span>/</span>
          <span>Reports</span>
        </div>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reimbursement Reports</h1>
            <p className="text-gray-600 mt-1">Comprehensive analytics and insights</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span className="font-medium text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Year Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Year</label>
            <select
              value={filters.year || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                year: e.target.value ? parseInt(e.target.value) : undefined
              }))}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Years</option>
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Month Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Month</label>
            <select
              value={filters.month || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                month: e.target.value ? parseInt(e.target.value) : undefined
              }))}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Months</option>
              {MONTHS.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>

          {/* Employee Filter */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">Employee</label>
            <input
              type="text"
              value={selectedEmployeeName || employeeSearch}
              onChange={(e) => {
                setEmployeeSearch(e.target.value)
                setSelectedEmployeeName('')
                setFilters(prev => ({ ...prev, employee_id: undefined }))
                setShowEmployeeDropdown(true)
              }}
              onFocus={() => setShowEmployeeDropdown(true)}
              placeholder="Search employee..."
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            {showEmployeeDropdown && employees.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                {employees.map(emp => (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => {
                      setFilters(prev => ({ ...prev, employee_id: emp.id }))
                      setSelectedEmployeeName(emp.full_name)
                      setEmployeeSearch('')
                      setShowEmployeeDropdown(false)
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex flex-col"
                  >
                    <span className="font-medium">{emp.full_name}</span>
                    <span className="text-xs text-gray-500">{emp.department || emp.email}</span>
                  </button>
                ))}
              </div>
            )}
            {filters.employee_id && (
              <button
                type="button"
                onClick={() => {
                  setFilters(prev => ({ ...prev, employee_id: undefined }))
                  setSelectedEmployeeName('')
                }}
                className="absolute right-2 top-7 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Department Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Department</label>
            <select
              value={filters.department || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                department: e.target.value || undefined
              }))}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.name} value={dept.name}>{dept.name}</option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
            <select
              value={filters.category_id || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                category_id: e.target.value || undefined
              }))}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                status: e.target.value || undefined
              }))}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear filters button */}
        {(filters.employee_id || filters.department || filters.category_id || filters.status || filters.month) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => {
                setFilters({ year: currentYear })
                setSelectedEmployeeName('')
                setEmployeeSearch('')
              }}
              className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'overview'
              ? 'bg-red-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('details')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'details'
              ? 'bg-red-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          Claims Detail
        </button>
      </div>

      {activeTab === 'overview' && report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Claims */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">Claims</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{report.summary.total_claims}</p>
              <p className="text-sm text-gray-500 mt-1">Total claims submitted</p>
            </div>

            {/* Total Submitted */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">Submitted</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(report.summary.total_submitted_amount)}</p>
              <p className="text-sm text-gray-500 mt-1">Total amount claimed</p>
            </div>

            {/* Total Approved */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Approved</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(report.summary.total_approved_amount)}</p>
              <p className="text-sm text-gray-500 mt-1">Total amount approved</p>
            </div>

            {/* Total Paid */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Paid</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(report.summary.total_paid_amount)}</p>
              <p className="text-sm text-gray-500 mt-1">Total amount paid out</p>
            </div>
          </div>

          {/* Secondary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Payment</p>
                <p className="text-lg font-bold text-orange-600">{formatCurrency(report.summary.pending_payment_amount)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">Average Claim</p>
                <p className="text-lg font-bold text-red-600">{formatCurrency(report.summary.average_claim_amount)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg. Processing</p>
                <p className="text-lg font-bold text-cyan-600">{report.summary.avg_processing_days} days</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">Rejection Rate</p>
                <p className="text-lg font-bold text-red-600">{report.summary.rejection_rate}%</p>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Status Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Claims by Status</h3>
              <div className="space-y-3">
                {Object.entries(report.by_status).map(([status, data]) => {
                  const colors = STATUS_COLORS[status] || STATUS_COLORS.draft
                  const percentage = report.summary.total_claims > 0
                    ? (data.count / report.summary.total_claims * 100)
                    : 0
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <div className="w-24 text-sm capitalize">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                          {status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${colors.bg.replace('100', '500')}`}
                          style={{ width: `${percentage}%`, backgroundColor: colors.text.replace('text-', '').replace('-700', '') }}
                        />
                      </div>
                      <div className="w-20 text-right">
                        <span className="text-sm font-medium text-gray-900">{data.count}</span>
                        <span className="text-xs text-gray-500 ml-1">({percentage.toFixed(0)}%)</span>
                      </div>
                      <div className="w-28 text-right text-sm text-gray-600">
                        {formatCurrency(data.amount)}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Monthly Trends */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h3>
              {report.monthly_trends.length > 0 ? (
                <div className="space-y-2">
                  {report.monthly_trends.map((trend) => (
                    <div key={trend.month} className="flex items-center gap-3">
                      <div className="w-12 text-sm text-gray-600">
                        {MONTHS[trend.month - 1]?.label.substring(0, 3)}
                      </div>
                      <div className="flex-1 flex gap-1 h-6">
                        {/* Submitted bar */}
                        <div
                          className="bg-blue-200 rounded-l"
                          style={{ width: `${maxMonthlyAmount > 0 ? (trend.submitted / maxMonthlyAmount * 100) : 0}%` }}
                          title={`Submitted: ${formatCurrency(trend.submitted)}`}
                        />
                        {/* Approved bar */}
                        <div
                          className="bg-green-300"
                          style={{ width: `${maxMonthlyAmount > 0 ? (trend.approved / maxMonthlyAmount * 100) : 0}%` }}
                          title={`Approved: ${formatCurrency(trend.approved)}`}
                        />
                        {/* Paid bar */}
                        <div
                          className="bg-emerald-500 rounded-r"
                          style={{ width: `${maxMonthlyAmount > 0 ? (trend.paid / maxMonthlyAmount * 100) : 0}%` }}
                          title={`Paid: ${formatCurrency(trend.paid)}`}
                        />
                      </div>
                      <div className="w-8 text-right text-sm text-gray-600">{trend.count}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No data available</p>
              )}
              <div className="flex justify-center gap-6 mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-blue-200" />
                  <span className="text-xs text-gray-600">Submitted</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-green-300" />
                  <span className="text-xs text-gray-600">Approved</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-emerald-500" />
                  <span className="text-xs text-gray-600">Paid</span>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown Tables Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Top Categories */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Categories</h3>
              {report.by_category.length > 0 ? (
                <div className="space-y-3">
                  {report.by_category.slice(0, 8).map((cat, index) => (
                    <div key={cat.id} className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 text-xs flex items-center justify-center font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{cat.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full bg-red-500"
                              style={{ width: `${maxCategoryAmount > 0 ? (cat.amount / maxCategoryAmount * 100) : 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{cat.count} claims</span>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(cat.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No data available</p>
              )}
            </div>

            {/* Top Claimants */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Claimants</h3>
              {report.by_employee.length > 0 ? (
                <div className="space-y-3">
                  {report.by_employee.slice(0, 8).map((emp, index) => (
                    <div key={emp.id} className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full bg-blue-500"
                              style={{ width: `${maxEmployeeAmount > 0 ? (emp.amount / maxEmployeeAmount * 100) : 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{emp.count} claims</span>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(emp.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No data available</p>
              )}
            </div>

            {/* By Department */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">By Department</h3>
              {report.by_department.length > 0 ? (
                <div className="space-y-3">
                  {report.by_department.slice(0, 8).map((dept, index) => (
                    <div key={dept.department} className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 text-xs flex items-center justify-center font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{dept.department}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full bg-green-500"
                              style={{ width: `${maxDepartmentAmount > 0 ? (dept.amount / maxDepartmentAmount * 100) : 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{dept.count} claims</span>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(dept.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No data available</p>
              )}
            </div>
          </div>

          {/* Payment Method Breakdown */}
          {report.by_payment_method.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {report.by_payment_method.map((pm) => (
                  <div key={pm.method} className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {pm.method.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500">{pm.count} payments</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(pm.amount)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'details' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Claim #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Period</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Submitted</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoadingClaims ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Loading claims...
                    </td>
                  </tr>
                ) : claims.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No claims found matching the selected filters.
                    </td>
                  </tr>
                ) : (
                  claims.map((claim) => {
                    const statusColors = STATUS_COLORS[claim.status] || STATUS_COLORS.draft
                    return (
                      <tr key={claim.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-red-600">{claim.claim_number}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{claim.user?.full_name}</p>
                            <p className="text-xs text-gray-500">{claim.department || claim.user?.department}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900 truncate max-w-xs">{claim.title}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {MONTHS[claim.period_month - 1]?.label.substring(0, 3)} {claim.period_year}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(claim.total_amount || claim.original_amount)}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors.bg} ${statusColors.text}`}>
                            {claim.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {claim.submitted_at ? format(new Date(claim.submitted_at), 'MMM d, yyyy') : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Link
                            to={`/reimbursement/claims/${claim.id}`}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} claims
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setClaimsPage(p => Math.max(1, p - 1))}
                  disabled={claimsPage === 1}
                  className="px-3 py-1 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setClaimsPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={claimsPage === pagination.pages}
                  className="px-3 py-1 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ReimbursementReportPage
